import { DotsThreeIcon, PlusIcon, UsersIcon } from '@phosphor-icons/react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { ErrorState } from '@/components/error-state';
import { InviteMemberDialog } from '@/components/invite-member-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { pushError } from '@/lib/faro';
import { orgBySlugQueryOptions, orgInvitationsQueryOptions, orgMembersQueryOptions } from '@/lib/org-query-options';
import {
  cancelInvitationServerFn,
  inviteMemberServerFn,
  type listInvitationsServerFn,
  type listMembersServerFn,
  removeMemberServerFn,
} from '@/server/functions/organization';

type MemberWithUser = Awaited<ReturnType<typeof listMembersServerFn>>[number];
type InvitationRow = Awaited<ReturnType<typeof listInvitationsServerFn>>[number];

function MembersSkeleton() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <UsersIcon />
          Members
        </h1>
        <Skeleton className="h-9 w-36 rounded" />
      </div>
      <div className="border border-mesa-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-28 rounded" />
                    <Skeleton className="h-3 w-40 rounded" />
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20 rounded" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-6 rounded" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/$orgSlug/members')({
  ssr: false,
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(orgMembersQueryOptions(context.org.id, context.org.slug)),
      context.queryClient.ensureQueryData(orgInvitationsQueryOptions(context.org.id, context.org.slug)),
    ]);
  },
  pendingComponent: MembersSkeleton,
  errorComponent: ErrorState,
  component: MembersPage,
});

function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const d = new Date(date);
  const diffInMs = now.getTime() - d.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 0) return `in ${Math.abs(diffInDays)} days`;
  return `${diffInDays} days ago`;
}

function getRoleDisplay(role: string | null): string {
  if (!role) return 'Member';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function MembersPage() {
  const { session, org: routeOrg } = Route.useRouteContext();
  const { orgSlug } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: queryOrg } = useSuspenseQuery(orgBySlugQueryOptions(orgSlug));
  const org = queryOrg ?? routeOrg;

  const { data: members } = useSuspenseQuery(orgMembersQueryOptions(org.id, org.slug));

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<MemberWithUser | null>(null);
  const [invitationToCancel, setInvitationToCancel] = useState<InvitationRow | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentUserMember = members.find((m) => m.userId === session.user.id);
  const isOwnerOrAdmin = currentUserMember?.role === 'owner' || currentUserMember?.role === 'admin';

  const { data: inviteList } = useSuspenseQuery(orgInvitationsQueryOptions(org.id, org.slug));
  const pendingInvitations = (inviteList ?? []).filter((inv) => inv.status === 'pending');

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    setIsRemoving(true);
    setError(null);

    try {
      await removeMemberServerFn({ data: { orgSlug: org.slug, memberId: memberToRemove.id } });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['org-members', org.id] }),
        queryClient.invalidateQueries({ queryKey: ['org-invitations', org.id] }),
      ]);
      setMemberToRemove(null);
    } catch (err) {
      pushError(err);
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!invitationToCancel) return;

    setIsCancelling(true);
    setError(null);

    try {
      await cancelInvitationServerFn({ data: { invitationId: invitationToCancel.id } });
      await queryClient.invalidateQueries({ queryKey: ['org-invitations', org.id] });
      setInvitationToCancel(null);
    } catch (err) {
      pushError(err);
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResendInvitation = async (invitation: InvitationRow) => {
    setResendingInvitationId(invitation.id);
    setError(null);
    try {
      await inviteMemberServerFn({
        data: {
          orgSlug: org.slug,
          email: invitation.email,
          role: (invitation.role ?? 'member') as 'owner' | 'admin' | 'member',
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['org-invitations', org.id] });
      toast.success(`Invitation resent to ${invitation.email}`);
    } catch (err) {
      pushError(err);
      toast.error(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setResendingInvitationId(null);
    }
  };

  const canRemoveMember = (member: MemberWithUser): boolean => {
    if (member.role === 'owner') return false;
    if (member.userId === session.user.id) return false;
    return isOwnerOrAdmin;
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <UsersIcon />
          Members
        </h1>
        {isOwnerOrAdmin && (
          <Button onClick={() => setIsInviteOpen(true)}>
            <PlusIcon className="h-4 w-4" />
            Invite member
          </Button>
        )}
      </div>

      {error && <div className="border border-mesa-red bg-mesa-red/10 p-4 text-sm text-mesa-red">{error}</div>}

      <div className="border border-mesa-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-mesa-muted py-8">
                  No members found
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{member.user.name}</span>
                      <span className="text-sm text-mesa-muted">{member.user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        member.role === 'owner'
                          ? 'text-mesa-green font-medium'
                          : member.role === 'admin'
                            ? 'text-blue-600'
                            : 'text-mesa-muted'
                      }
                    >
                      {getRoleDisplay(member.role)}
                    </span>
                  </TableCell>
                  <TableCell className="text-mesa-muted">{formatRelativeTime(member.createdAt)}</TableCell>
                  <TableCell>
                    {canRemoveMember(member) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <DotsThreeIcon className="h-4 w-4" />
                            <span className="sr-only">Actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-mesa-red focus:text-mesa-red"
                            onClick={() => setMemberToRemove(member)}
                          >
                            Remove member
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pendingInvitations.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-medium">Pending invitations</h2>
          <div className="border border-mesa-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingInvitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.email}</TableCell>
                    <TableCell className="text-mesa-muted">{getRoleDisplay(invitation.role)}</TableCell>
                    <TableCell className="text-mesa-muted">{formatRelativeTime(invitation.createdAt)}</TableCell>
                    <TableCell className="text-mesa-muted">
                      {new Date(invitation.expiresAt) < new Date() ? (
                        <span className="inline-flex items-center gap-2">
                          <Badge variant="destructive">Expired</Badge>
                          {formatRelativeTime(invitation.expiresAt)}
                        </span>
                      ) : (
                        formatRelativeTime(invitation.expiresAt)
                      )}
                    </TableCell>
                    <TableCell>
                      {isOwnerOrAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <DotsThreeIcon className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={resendingInvitationId === invitation.id}
                              onClick={() => handleResendInvitation(invitation)}
                            >
                              {resendingInvitationId === invitation.id ? 'Resending...' : 'Resend invitation'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-mesa-red focus:text-mesa-red"
                              onClick={() => setInvitationToCancel(invitation)}
                            >
                              Cancel invitation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <InviteMemberDialog
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        orgSlug={org.slug}
        currentUserRole={currentUserMember?.role}
        onInvited={() => {
          void queryClient.invalidateQueries({ queryKey: ['org-members', org.id] });
          void queryClient.invalidateQueries({ queryKey: ['org-invitations', org.id] });
        }}
      />

      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {memberToRemove?.user.name} from this organization? They will lose access
              to all repositories.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={isRemoving}
            >
              {isRemoving ? 'Removing...' : 'Remove member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!invitationToCancel} onOpenChange={(open) => !open && setInvitationToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation to {invitationToCancel?.email}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvitation}
              className="bg-mesa-red text-white hover:bg-mesa-red/90"
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling...' : 'Cancel invitation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
